/**
 * Main entry point for the ShuttleNomix backend application.
 * - Bootstraps the root AppComponent using Angular's standalone API.
 * - Registers global providers for Ionic, Angular Forms, HTTP, Firebase, Service Worker, and error handling.
 * - Enables production mode optimizations when applicable.
 * - Only essential bootstrapping logic and providers should be present in main.ts.
 * - Application routes are defined to ensure all pages are included in the build.
 */
/* eslint-disable no-console */
import { enableProdMode, importProvidersFrom, ErrorHandler } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import type { Provider, EnvironmentProviders } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { IonicStorageModule } from '@ionic/storage-angular';
import { provideRouter } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { provideHttpClient } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { Title } from '@angular/platform-browser';
import 'zone.js';

// Firebase imports
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideFirestore, getFirestore } from '@angular/fire/firestore';
// Note: Firebase Realtime Database removed - app uses Firestore exclusively (saves ~105KB)
import { provideStorage as provideFirebaseStorage, getStorage } from '@angular/fire/storage';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideFunctions, getFunctions } from '@angular/fire/functions';
import { HttpClient } from '@angular/common/http';
import 'zone.js';

// Shared imports
import { GlobalErrorHandlerService } from '@shared/services';
import { environment } from '@shared/environment';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';

if (environment.production) {
    enableProdMode();
}

async function bootstrapApp() {
    console.log('✅ Initializing main.ts.bootstrapApp()...');
    console.time('✅ Bootstrap Duration');

    // Common providers
    // const providers = [
    const providers: Array<Provider | EnvironmentProviders> = [
        Title,
        importProvidersFrom(
            IonicModule.forRoot(),
            FormsModule,
            IonicStorageModule.forRoot()
        ),
        provideHttpClient(),
        provideRouter(routes),
        provideServiceWorker('ngsw-worker.js', {
            enabled: environment.production,
            registrationStrategy: 'registerWhenStable:30000',
        }),
        { provide: ErrorHandler, useClass: GlobalErrorHandlerService }
    ];

    // Firebase Initialization
    try {
        console.log('✅ Registering Firebase providers with Angular DI (no direct getApps/getApp calls)...');

        providers.push(
            provideFirebaseApp(() => {
                console.log('✅ Initializing Firebase via provideFirebaseApp()');
                return initializeApp(environment.firebase);
            }),
            provideAuth(() => getAuth()),
            provideFirestore(() => getFirestore()),
            provideFirebaseStorage(() => getStorage()),
            provideFunctions(() => getFunctions(undefined, 'us-central1'))
        );

        console.log('✅ Firebase providers registered with Angular DI');
    } catch (firebaseError) {
        console.error('❌ Firebase initialization failed during provider registration:', firebaseError);
        console.warn('⚠️ Continuing bootstrap without Firebase providers');
    }

    // Bootstrap the AppComponent
    try {
        console.log('✅ Bootstrapping application with providers...');
        await bootstrapApplication(AppComponent, { providers });
        console.log('✅ Application successfully bootstrapped');
    } catch (bootstrapError) {
        console.error('❌ Fatal bootstrap error:', bootstrapError);
        console.error('Stack trace:', bootstrapError instanceof Error
            ? bootstrapError.stack
            : 'No stack trace available');
        console.timeEnd('✅ Bootstrap Duration');
        return; // stop further execution
    }

    // The AppComponent has been successfully bootstrapped.
    // Initialize AppCheck.
    try {
        if (environment.production) {
            console.log('Initializing Firebase AppCheck post-bootstrap...');
            // Call with undefined: Firebase SDK uses the default app.
            initializeAppCheck(undefined, {
                provider: new ReCaptchaV3Provider(environment.recaptchaSiteKey),
                isTokenAutoRefreshEnabled: true
            });
            console.log('✅ Firebase AppCheck initialized');
        }
    } catch (appCheckError) {
        console.error('❌ Failed to initialize Firebase AppCheck:', appCheckError);
    }

    console.timeEnd('✅ Bootstrap Duration');
}

bootstrapApp();

// bootstrapApplication(AppComponent, {
//     providers: [
//         Title,
//         importProvidersFrom(
//             IonicModule.forRoot(),
//             FormsModule,
//             HttpClient,
//             IonicStorageModule.forRoot()
//         ),
//         // provideRouter(), // Add your routes here
//         provideServiceWorker('ngsw-worker.js', {
//             enabled: environment.production,
//             registrationStrategy: 'registerWhenStable:30000'
//         }),
//         provideFirebaseApp(() => initializeApp(environment.firebase)),
//         provideFirestore(() => getFirestore()),
//         provideDatabase(() => getDatabase()),
//         provideFirebaseStorage(() => getStorage()),
//         provideAuth(() => getAuth()),
//         provideFunctions(() => getFunctions()),
//             { provide: GlobalErrorHandlerService, useClass: GlobalErrorHandlerService }
//     ]
// })
